import { Box, Flex, Grid, Stack, Text } from '@chakra-ui/react';
import { Button } from '@time-line/components/common';
import { PauseCircle, Refresh } from '@time-line/components/icons';
import { useAnalytics } from 'hooks/useAnalytics';
import { CartUpsell } from 'lib/analytics/constants';
import Img from 'next/image';
import { useEffectOnce } from 'react-use';
import type { CartLineUpdateInput } from 'services/shopify';
import { theme } from 'theme';
import type { CartLineItem } from 'types/cart/CartLineItem';
import type { CartLineItemWithServings } from 'types/cart/CartLineItemWithServings';
import type {
  SubscriptionUpsellOption,
  SubscriptionUpsellOptionWithServings,
} from 'types/product/SubscriptionUpsellOption';
import { getPricePerMonth } from 'utils/getPricePerMonth';
import { getSavingsRate } from 'utils/getSavingsRate';

import { hasServings, isOneTimePurchase } from '../utils/typeInference';
import { PriceComparison } from './PriceComparison';

interface CartUpsellProps {
  lineItem: CartLineItemWithServings | CartLineItem;
  subscription: SubscriptionUpsellOption | SubscriptionUpsellOptionWithServings;
  onUpdateItem: (input: Omit<CartLineUpdateInput, 'id'>) => void;
  isUpdatingItem?: boolean;
}

const getSellingPlanAmount = (
  lineItem: CartLineItem | CartLineItemWithServings,
) => lineItem.sellingPlanAllocation?.priceAdjustments[0].price.amount;

const getOtpAmount = (lineItem: CartLineItem | CartLineItemWithServings) =>
  lineItem.cost.totalAmount.amount / lineItem.quantity;

const getUpsellAttribs = (lineItem: CartLineItem) => [
  {
    key: '__timeline_upsell_originalCartLine',
    value: JSON.stringify({
      merchandiseId: lineItem.merchandise.id,
      sellingPlanId: lineItem.sellingPlanAllocation?.sellingPlan.id,
      quantity: lineItem.quantity,
    }),
  },
  {
    key: '__timeline_upsell_rule',
    value: isOneTimePurchase(lineItem)
      ? 'OTP_TO_SUBSCRIPTION'
      : 'NEXT_SUBSCRIPTION',
  },
];

export function UpsellSubscriptionCard({
  lineItem,
  subscription,
  onUpdateItem,
  isUpdatingItem = false,
}: CartUpsellProps) {
  const { analytics } = useAnalytics();
  /* If the the line item is already a Subscription, use its subscription
   * price, otherwise use the One Time Purchase price. */
  const amountBefore = getSellingPlanAmount(lineItem) || getOtpAmount(lineItem);
  const upsellAmount = subscription.price.amount;

  const pricePerMonthBefore = hasServings(lineItem.merchandise)
    ? getPricePerMonth(lineItem.merchandise.servings, amountBefore)
    : amountBefore;

  const pricePerMonthAfter = hasServings(subscription)
    ? getPricePerMonth(subscription.servings, upsellAmount)
    : upsellAmount;

  const saving = getSavingsRate({
    anchor: pricePerMonthBefore,
    price: pricePerMonthAfter,
  });

  const product = lineItem.merchandise.product.handle;

  const analyticsType = isOneTimePurchase(lineItem)
    ? {
        display: CartUpsell.OTP2SUB_DISPLAY,
        accepted: CartUpsell.OTP2SUB_ACCEPTED,
      }
    : {
        display: CartUpsell.NEXT_SUB_DISPLAY,
        accepted: CartUpsell.NEXT_SUB_ACCEPTED,
      };

  useEffectOnce(() => {
    analytics.cartUpsellDisplay({
      type: analyticsType.display,
      product,
    });
  });

  return (
    <Box
      sx={{
        containerType: 'inline-size',
      }}
    >
      <Grid
        justifyContent="space-between"
        flexWrap="wrap"
        alignItems="center"
        sx={{
          gap: '2xs',
          gridTemplateColumns: '1fr',
          [`@container (min-width: ${theme.breakpoints.sm})`]: {
            '&': {
              gap: 'sm',
              gridTemplateColumns: '1fr auto',
            },
          },
        }}
      >
        {isOneTimePurchase(lineItem) && (
          <Flex flexDirection="column" gap="3xs">
            {!!saving && (
              <Text size="detail" color="red.300" fontWeight="medium">
                Subscribe &amp; Save an extra {saving}%
              </Text>
            )}
            <Stack borderColor="blackAlpha.200" spacing="5xs">
              {hasServings(subscription) && (
                <Flex alignItems="center" gap="3xs">
                  <Refresh boxSize="xs" />
                  <Text size="detail">{subscription.sellingPlan.title}</Text>
                </Flex>
              )}
              <Flex alignItems="center" gap="3xs">
                <PauseCircle boxSize="xs" />
                <Text size="detail">
                  Pause, reschedule or cancel at any time
                </Text>
              </Flex>
            </Stack>
            <PriceComparison
              before={pricePerMonthBefore}
              quantity={lineItem.quantity}
              after={pricePerMonthAfter}
              total={subscription.price.amount}
            />
          </Flex>
        )}
        {!isOneTimePurchase(lineItem) && (
          <Flex
            sx={{
              gap: '2xs',
              [`@container (min-width: ${theme.breakpoints.sm})`]: {
                '&': {
                  gap: 'sm',
                },
              },
            }}
          >
            {subscription.imageUrl && (
              <Box boxSize="2xl">
                <Img
                  alt={`${lineItem.merchandise.title} of ${lineItem.merchandise.product.title}`}
                  src={subscription.imageUrl}
                  height={175}
                  width={175}
                  sizes="175px"
                />
              </Box>
            )}
            <Stack spacing="5xs" justifyContent="center">
              {!!saving && (
                <Text size="detail" color="red.300" fontWeight="medium">
                  Upgrade &amp; Save an extra {saving}%
                </Text>
              )}
              <Text size="detail" color="blackAlpha.700">
                {subscription.sellingPlan.title}
              </Text>
              <PriceComparison
                quantity={lineItem.quantity}
                before={pricePerMonthBefore}
                after={pricePerMonthAfter}
                total={subscription.price.amount}
              />
            </Stack>
          </Flex>
        )}
        <Button
          className="button--add-to-cart"
          isLoading={isUpdatingItem}
          onClick={() => {
            onUpdateItem({
              merchandiseId: subscription.id,
              sellingPlanId: subscription.sellingPlan.id,
              attributes: getUpsellAttribs(lineItem),
            });
            analytics.cartUpsellAccepted({
              type: analyticsType.accepted,
              product,
              variant: subscription.sellingPlan.title,
            });
          }}
          variant="primary"
          size="sm"
          sx={{
            width: '100%',
            [`@container (min-width: ${theme.breakpoints.sm})`]: {
              '&': {
                width: 'auto',
              },
            },
          }}
        >
          Upgrade now
        </Button>
      </Grid>
    </Box>
  );
}
